/**
 * O resultado auditado do caso (E153, GUS-176, ADR-0104): o contrato de
 * `GET /api/casos/{id}/resultado`, espelhado à mão dos tipos do servidor e preso pelo teste de
 * paridade (`tests/unit/test_e153_f3_contrato_web.py`).
 *
 * A tela só mostra o que o servidor devolve: a situação do resultado e a etapa `resultado` conferida
 * agora, o snapshot da última avaliação (identidade, versões, conferência de veracidade, evidência,
 * requisitos por leitura com o estado interno e a matriz pública, decisão e achados), o que cada
 * saída pode consumir e os artefatos registrados. Nada aqui decide, registra ou compõe.
 */
import axios, { type AxiosInstance } from 'axios';

import type { TipoDaPendencia } from '@/lib/conferencia_da_leitura';
import type { EtapaDoCasoData, EtapaDoFluxo } from '@/lib/conferencia_das_etapas';
import type {
  CategoriaDoRequisito,
  FonteDaForma,
  ModoDoExame,
  PapelDoRequisito,
  PendenciaData,
} from '@/lib/formalizacao';
import type {
  AchadoData,
  AfirmacaoData,
  ConclusaoDaFormalizacao,
  DecisaoDaRevisao,
  DiligenciaData,
  ElementoDeProvaData,
  EstadoDaAnalise,
  EstadoDaVeracidade,
  IdDaRota,
  LadoDoElemento,
  LocalizacaoDaClausulaData,
  MotivoDaPendencia,
  RevisaoEmVigorData,
  RevisaoPendenteData,
  SituacaoDaLeitura,
  SituacaoDoElemento,
  SituacaoDoRequisito,
  TipoDaRevisao,
  TipoDoElemento,
  VeracidadeDaAvaliacaoData,
} from '@/lib/formalizacaoAvaliacao';

export type ResultadoPublico =
  | 'atendido'
  | 'nao_violada'
  | 'parcial'
  | 'pendente'
  | 'nao_demonstrado'
  | 'nao_se_aplica'
  | 'irregular';
export type ReasonCode =
  | 'REGULAR_ROUTE'
  | 'REQ_SUPPLIED'
  | 'NOT_VIOLATED'
  | 'IRREGULAR_REQ'
  | 'REQ_NOT_REQUIRED'
  | 'PENDING'
  | 'NATURE_UNMAPPED'
  | 'NOT_DEMONSTRATED';
export type SuporteDoRequisito = 'primario' | 'suplementar';
export type ClasseDoFato = 'declarado' | 'verificado' | 'consulta_externa';
export type OrigemDoModo = 'cadastro_do_caso';
export type SituacaoDaBusca =
  | 'nao_executada'
  | 'nao_conferida'
  | 'com_achados'
  | 'sem_achados_no_alcance';
export type SituacaoDoResultado =
  | 'aguardando_processamento'
  | 'pendencia_tecnica'
  | 'em_conferencia'
  | 'preparado';
export type TipoDeSaida = 'laudo' | 'resumo' | 'evidencias' | 'riscos';
export type RegimeDaSaida = 'liberado' | 'reservado';
export type EtapaDoArtefato = 'rascunho' | 'final';
export type AprovacaoDoArtefato = 'minuta' | 'conclusao_definida';
/** E170.F2: na versão vigente, só o selecionado é vigente; os anteriores dela, substituídos. */
export type SituacaoDoArtefato = 'vigente' | 'substituido' | 'desatualizado';
export type TipoDaFonte =
  | 'standard'
  | 'contested'
  | 'protocolo'
  | 'origem'
  | 'pacote'
  | 'evidencia';

/** A ordem das saídas, a do servidor (`TIPOS_DE_SAIDA`). */
export const TIPOS_DE_SAIDA: readonly TipoDeSaida[] = ['laudo', 'resumo', 'evidencias', 'riscos'];

export interface IdentidadeDoResultadoData {
  escritorio_id: string;
  case_id: string;
  display_code: string;
  pecas: string[];
  modo: ModoDoExame;
  origem_do_modo: OrigemDoModo;
  rodada: number;
  avaliacao_id: string;
  resultado_sha256: string;
  entrada_sha256: string;
  fluxo_sha256: string | null;
  criada_por: string | null;
  criada_em: string;
}

export interface VersoesDoResultadoData {
  catalogo_versao: string;
  catalogo_sha256: string;
  criterio_do_grau: string;
  veracidade: string;
  interpretacao: string;
  regra_das_etapas: number;
}

export interface FonteDoResultadoData {
  id: string;
  tipo: TipoDaFonte;
  nome: string;
  sha256: string | null;
}

export interface FatoDoResultadoData {
  elemento: ElementoDeProvaData;
  classe: ClasseDoFato;
}

export interface ImagemDoResultadoData {
  elemento_id: string;
  tipo: TipoDoElemento;
  origem: 'sistema';
  situacao: SituacaoDoElemento;
  decisao_do_perito: DecisaoDaRevisao | null;
}

export interface EvidenciaDoResultadoData {
  fontes: FonteDoResultadoData[];
  fatos: FatoDoResultadoData[];
  nao_extraidos: ElementoDeProvaData[];
  inferencias: AfirmacaoData[];
  imagens: ImagemDoResultadoData[];
}

/** E172.F1 (GUS-230 B2.4): a revisão vigente sobre o elemento ou sobre o par da rota. */
export interface RevisaoDoElementoData {
  tipo: TipoDaRevisao;
  decisao: DecisaoDaRevisao;
  em: string | null;
  localizacao: LocalizacaoDaClausulaData | null;
}

export interface ElementoDoRequisitoData {
  id: string;
  tipo: TipoDoElemento;
  lado: LadoDoElemento;
  situacao: SituacaoDoElemento;
  instante: string | null;
  carimbo: string | null;
  revisao_exigida: boolean;
  revisao: RevisaoDoElementoData | null;
  secoes: number[];
  /** E172.F8 (ADR-0126 §5): a cláusula localizada pelo sistema na CCB; omitida sem ela. */
  localizacao?: LocalizacaoDaClausulaData;
}

export interface MatrizPublicaData {
  resultado: ResultadoPublico;
  reason_code: ReasonCode;
  consideracoes: string;
}

export interface RequisitoDoResultadoData {
  requisito_id: string;
  titulo: string;
  papel: PapelDoRequisito;
  categoria: CategoriaDoRequisito;
  situacao: SituacaoDoRequisito;
  suporte: SuporteDoRequisito | null;
  rota: IdDaRota | null;
  elementos: ElementoDoRequisitoData[];
  faltam: string[];
  convergencia_pessoa_e_ato: boolean;
  motivo: string;
  publica: MatrizPublicaData;
  revisao_da_rota: RevisaoDoElementoData | null;
}

export interface LeituraDoResultadoData {
  perfil_id: string;
  leitura_id: string;
  data: string | null;
  titulo: string;
  fonte_da_forma: FonteDaForma;
  prioridade: number;
  subsidiaria: boolean;
  situacao: SituacaoDaLeitura;
  escolhida: boolean;
  requisitos: RequisitoDoResultadoData[];
  motivo: string;
}

export interface DecisaoDoResultadoData {
  conclusao: ConclusaoDaFormalizacao;
  proposta: ConclusaoDaFormalizacao | null;
  motivos_da_pendencia: MotivoDaPendencia[];
  leitura_escolhida: string | null;
  razao_da_escolha: string | null;
  diligencias: DiligenciaData[];
  revisoes_pendentes: RevisaoPendenteData[];
  revisoes_em_vigor: RevisaoEmVigorData[];
  pendencias_normativas: PendenciaData[];
  juizo_pendente: boolean;
}

export interface AchadosDoResultadoData {
  estado: EstadoDaAnalise;
  criterio: string;
  cobertura: string;
  achados: AchadoData[];
  laudo_essencial: string[];
  riscos_reservados: string[];
  situacao_da_busca: SituacaoDaBusca;
  reservado_a_compor: boolean;
}

export interface ResultadoAuditadoData {
  schema: 'resultado-auditado-v1';
  identidade: IdentidadeDoResultadoData;
  versoes: VersoesDoResultadoData;
  qa: VeracidadeDaAvaliacaoData;
  evidencia: EvidenciaDoResultadoData;
  leituras: LeituraDoResultadoData[];
  decisao: DecisaoDoResultadoData;
  achados: AchadosDoResultadoData;
}

export interface ConsumoPorKindData {
  kind: TipoDeSaida;
  regime: RegimeDaSaida;
  assinatura_aplicavel: boolean;
  disponivel: boolean;
  motivo: string | null;
}

/** E216.F5.C01: uma linha da seção 2 no manifesto do artefato. */
export interface ArquivoDaSecao2Data {
  sha256: string;
  arquivo: string | null;
}

export interface ManifestoDoArtefatoData {
  originais: string[];
  recortes: string[];
  rascunhos: string[];
  finais: string[];
  /** E216.F5.C01: os arquivos da seção 2 do exame com que os bytes foram compostos (o ZIP sai
   * deles); ausente fora do exame e no artefato de antes. */
  secao_2?: ArquivoDaSecao2Data[];
}

export interface ArtefatoData {
  id: string;
  case_id: string;
  kind: TipoDeSaida;
  avaliacao_id: string;
  avaliacao_versao: number;
  resultado_sha256: string;
  versao: number;
  layout: string;
  qa: EstadoDaVeracidade;
  aprovacao: AprovacaoDoArtefato;
  etapa: EtapaDoArtefato;
  bytes_sha256: string;
  emissao_id: string | null;
  regime: RegimeDaSaida;
  assinatura_aplicavel: boolean;
  manifesto: ManifestoDoArtefatoData;
  criada_por: string | null;
  criada_em: string;
  situacao: SituacaoDoArtefato;
}

/** E162.F1.T06 (ADR-0111 §2.6): o que fazer agora, uma ação por caso. */
export type ProximoPasso =
  | 'receber'
  | 'verificar'
  | 'concluir_ccb'
  | 'conferir_leitura'
  | 'corrigir_etapa'
  | 'revisar_proposta'
  | 'homologar'
  | 'conferir_saida'
  | 'revisar_saidas'
  | 'assinar'
  | 'liberar'
  | 'informar_banco'
  | 'enviar_arquivos'
  // E205.F3: só das pendências informativas (`bloqueia: false`), nunca o próximo passo.
  | 'confirmar_leitura'
  | 'conferir_apoio';

/** Por que o caso não está pronto, na ordem em que se resolve. */
export type MotivoDaProntidao =
  | 'sem_ccb'
  | 'exame_pendente'
  | 'verificacao_desalinhada'
  | 'regua_anterior'
  | 'conclusao_pendente'
  | 'leitura_em_conferencia'
  | 'etapa_em_conferencia'
  | 'avaliacao_pendente'
  | 'juizo_pendente'
  | 'revisao_pendente'
  | 'saida_nao_conferida'
  | 'saida_reprovada'
  | 'saidas_nao_revisadas'
  | 'assinatura_pendente'
  | 'banco_pendente'
  | 'aguardando_arquivos'
  // E205.F3 (ADR-0147 §5): as informativas, que não seguram saída nenhuma.
  | 'leitura_proposta'
  | 'apoio_a_conferir'
  | 'ponto_do_perito';

/**
 * E205.F3: onde a pendência informativa se resolve na mesa (a aba, o bloco, o documento, o campo).
 * E205.F3.C01/C02: o campo é o par (`campo`, `referente_id`), e `natureza` é o tipo do ponto do
 * perito; ausentes nas respostas anteriores a elas.
 */
export interface AlvoDaPendenciaData {
  aba: 'exame' | 'pendencias' | 'documentos' | 'fotografias' | 'conclusao';
  ancora: string | null;
  documento_id: string | null;
  campo: string | null;
  referente_id?: string | null;
  natureza?: TipoDaPendencia | null;
}

export interface PendenciaDaProntidaoData {
  motivo: MotivoDaProntidao;
  passo: ProximoPasso;
  peca_id: string | null;
  peca: string | null;
  etapa: EtapaDoFluxo | null;
  /**
   * E184.F4 (GUS-145 item 7): no juízo pendente, o que falta; E193.F1.C15, no caso que aguarda
   * arquivos, os arquivos que faltam; ausente nas outras pendências.
   */
  faltam?: string[];
  /** E205.F3: `false` nas informativas (vêm no fim da lista); ausente, bloqueia como sempre. */
  bloqueia?: boolean;
  /** E205.F3: onde a informativa se resolve; ausente nas outras (elas seguem o passo). */
  alvo?: AlvoDaPendenciaData | null;
}

/** E205.F3: a pendência segura alguma saída (as informativas vêm com `bloqueia: false`). */
export function bloqueia(pendencia: PendenciaDaProntidaoData): boolean {
  return pendencia.bloqueia !== false;
}

export interface LiberacaoDaSaidaData {
  liberada: boolean;
  motivo: MotivoDaProntidao | null;
  /** As peças nomeadas pelo motivo; no juízo pendente, o que falta (E184.F4). */
  pendentes: string[];
}

/** O próximo passo, as pendências na ordem e a liberação de cada saída — a régua dos portões. */
export interface ProntidaoDoCasoData {
  proximo_passo: ProximoPasso;
  pendencias: PendenciaDaProntidaoData[];
  proposta: LiberacaoDaSaidaData;
  laudo: LiberacaoDaSaidaData;
  conferencia_da_saida: LiberacaoDaSaidaData;
  assinatura: LiberacaoDaSaidaData;
}

export interface ResultadoDoCasoData {
  case_id: string;
  situacao: SituacaoDoResultado;
  consumivel: boolean;
  etapa: EtapaDoCasoData | null;
  resultado: ResultadoAuditadoData | null;
  kinds: ConsumoPorKindData[];
  artefatos: ArtefatoData[];
  /** `null` quando a conferência das etapas falhou agora (o retrato do caso não foi lido). */
  prontidao: ProntidaoDoCasoData | null;
}

export type MotivoDaFalhaDoResultado =
  | 'sem_permissao'
  | 'nao_encontrado'
  | 'fora_da_raia'
  | 'indisponivel';

export class ResultadoDoCasoError extends Error {
  readonly motivo: MotivoDaFalhaDoResultado;

  constructor(motivo: MotivoDaFalhaDoResultado) {
    super(`resultado do caso: ${motivo}`);
    this.name = 'ResultadoDoCasoError';
    this.motivo = motivo;
  }
}

const _POR_STATUS: Readonly<Record<number, MotivoDaFalhaDoResultado>> = {
  403: 'sem_permissao',
  404: 'nao_encontrado',
  422: 'fora_da_raia',
};

function motivoDoErro(err: unknown): MotivoDaFalhaDoResultado {
  if (!axios.isAxiosError(err) || err.response === undefined) return 'indisponivel';
  return _POR_STATUS[err.response.status] ?? 'indisponivel';
}

export async function lerResultadoDoCaso(
  client: AxiosInstance,
  caseId: string,
): Promise<ResultadoDoCasoData> {
  try {
    const { data } = await client.get<ResultadoDoCasoData>(`/casos/${caseId}/resultado`);
    return data;
  } catch (err) {
    throw new ResultadoDoCasoError(motivoDoErro(err));
  }
}

/** A leitura que sustenta a conclusão ou a proposta, como o servidor a marcou. */
export function leituraEscolhida(r: ResultadoAuditadoData): LeituraDoResultadoData | undefined {
  return r.leituras.find((l) => l.escolhida);
}

/** As leituras na ordem da tela: a escolhida primeiro, as demais na ordem do servidor. */
export function leiturasNaOrdem(r: ResultadoAuditadoData): LeituraDoResultadoData[] {
  return [...r.leituras].sort((a, b) => Number(b.escolhida) - Number(a.escolhida));
}

/** Os fatos por classe, na ordem das classes. */
export function fatosPorClasse(e: EvidenciaDoResultadoData): Record<ClasseDoFato, number> {
  const contagem: Record<ClasseDoFato, number> = {
    declarado: 0,
    verificado: 0,
    consulta_externa: 0,
  };
  for (const f of e.fatos) contagem[f.classe] += 1;
  return contagem;
}

/** Os oito primeiros caracteres: o bastante para a equipe conferir contra o registro. */
export function codigoCurto(sha256: string | null): string {
  return sha256 === null ? '' : sha256.slice(0, 12);
}
